import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Button,
  CircularProgress,
  Grid,
  TextField,
} from "@material-ui/core";
import { Field, Form, Formik } from "formik";
import { toast } from "react-toastify";
import axios from "axios";
import {
  Code2,
  Info,
  MessageSquare,
  Image as ImageIcon,
  Send,
  Upload,
  ListOrdered,
  AlertCircle,
} from "lucide-react";

import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import usePlans from "../../hooks/usePlans";
import { AuthContext } from "../../context/Auth/AuthContext";
import { BACKEND_URL } from "../../config/env";

const useStyles = makeStyles((theme) => {
  const ds = theme.chatiaDS || {};
  const dsColors = ds.colors || {};
  const isDark = theme.mode === "dark" || theme.palette?.type === "dark";
  return {
    container: {
      flex: 1,
      minHeight: 0,
      height: "100%",
      padding: 12,
      overflowY: "auto",
      overflowX: "hidden",
      display: "flex",
      flexDirection: "column",
      gap: 14,
      ...(theme.scrollbarStyles || {}),
    },
    pageHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      flexWrap: "wrap",
      padding: "4px 4px 0",
    },
    headerLeft: {
      display: "flex",
      alignItems: "center",
      gap: 12,
    },
    pageIconWrap: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 36,
      height: 36,
      borderRadius: 10,
      background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
      color: theme.palette.primary.main,
    },
    pageTitle: {
      fontSize: 18,
      fontWeight: 600,
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#1a1a1a"),
      lineHeight: 1.2,
    },
    pageSubtitle: {
      fontSize: 12,
      color:
        dsColors.textSecondary ||
        (isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.55)"),
      marginTop: 2,
    },

    card: {
      borderRadius: 12,
      background: dsColors.surfaceDefault || (isDark ? "#11141b" : "#f7f8fa"),
      border: `1px solid ${
        dsColors.border || (isDark ? "#202632" : "#e8e8e8")
      }`,
      boxShadow: isDark
        ? "0 2px 12px rgba(0,0,0,0.35)"
        : "0 2px 12px rgba(15,23,42,0.06)",
      padding: 16,
    },
    cardHeader: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      marginBottom: 14,
    },
    cardHeaderIcon: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 30,
      height: 30,
      borderRadius: 8,
      background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
      color: theme.palette.primary.main,
    },
    cardHeaderTitle: {
      fontSize: 13.5,
      fontWeight: 600,
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#1a1a1a"),
      lineHeight: 1.2,
    },
    cardHeaderSubtitle: {
      fontSize: 11.5,
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      marginTop: 1,
    },

    sectionTitle: {
      fontSize: 12,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: 0.6,
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      marginBottom: 8,
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
    },
    listInfo: {
      margin: 0,
      paddingLeft: 18,
      fontSize: 13,
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
      lineHeight: 1.7,
      "& li": { marginBottom: 4 },
      "& ul": {
        marginTop: 4,
        paddingLeft: 18,
        color:
          dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      },
    },

    // Docs panel (endpoint info)
    docs: {
      background: isDark ? "rgba(255,255,255,0.02)" : "rgba(15,23,42,0.03)",
      borderRadius: 10,
      padding: 12,
      fontSize: 13,
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
      display: "flex",
      flexDirection: "column",
      gap: 8,
    },
    docsRow: {
      display: "flex",
      gap: 8,
      alignItems: "baseline",
    },
    docsLabel: {
      fontWeight: 700,
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      minWidth: 78,
      fontSize: 12,
      textTransform: "uppercase",
      letterSpacing: 0.5,
    },
    docsValue: {
      flex: 1,
      wordBreak: "break-all",
      fontFamily:
        "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      fontSize: 12.5,
    },
    docsBody: {
      background: isDark ? "rgba(0,0,0,0.25)" : "rgba(15,23,42,0.04)",
      borderRadius: 8,
      padding: 10,
      fontSize: 12,
      lineHeight: 1.5,
      fontFamily:
        "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      overflowX: "auto",
      ...(theme.scrollbarStyles || {}),
    },
    docsHint: {
      fontSize: 12,
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
    },

    fieldsList: {
      margin: 0,
      paddingLeft: 18,
      fontSize: 13,
      lineHeight: 1.6,
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
      "& li": { marginBottom: 2 },
      "& code": {
        fontFamily:
          "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        fontSize: 12,
        background: isDark
          ? "rgba(255,255,255,0.06)"
          : "rgba(15,23,42,0.06)",
        padding: "1px 6px",
        borderRadius: 4,
      },
    },

    // Form
    testTitle: {
      fontSize: 12.5,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: 0.6,
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      marginBottom: 10,
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
    },
    roundedField: {
      "& .MuiOutlinedInput-root": {
        borderRadius: 12,
        background: isDark ? "rgba(255,255,255,0.02)" : "#fff",
      },
      "& .MuiOutlinedInput-input": {
        padding: "12px 14px",
      },
    },
    fileFieldWrap: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      borderRadius: 12,
      padding: "10px 12px",
      border: `1px dashed ${
        dsColors.border || (isDark ? "#2a3140" : "#cbd5e1")
      }`,
      background: isDark ? "rgba(255,255,255,0.02)" : "#fff",
    },
    fileBtn: {
      borderRadius: 10,
      padding: "8px 12px",
      fontSize: 12.5,
      fontWeight: 600,
      gap: 6,
      cursor: "pointer",
      border: "none",
      display: "inline-flex",
      alignItems: "center",
      transition: "all 0.18s ease",
      background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#0f172a"),
      "&:hover": {
        background: isDark ? "rgba(255,255,255,0.1)" : "rgba(15,23,42,0.1)",
      },
    },
    fileHint: {
      fontSize: 12,
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      flex: 1,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    fileInput: { display: "none" },
    sendBtn: {
      borderRadius: 12,
      textTransform: "none",
      fontWeight: 500,
      fontSize: 13,
      padding: "10px 18px",
      gap: 6,
      minHeight: 40,
      boxShadow: "none",
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText || "#fff",
      "&:hover": {
        boxShadow: "none",
        backgroundColor: theme.palette.primary.dark,
      },
    },
    sendRow: {
      display: "flex",
      justifyContent: "flex-end",
      marginTop: 6,
    },
  };
});

const MessagesAPI = () => {
  const classes = useStyles();
  const history = useHistory();

  const [formMessageTextData] = useState({
    token: "",
    number: "",
    body: "",
    userId: "",
    queueId: "",
  });
  const [formMessageMediaData] = useState({
    token: "",
    number: "",
    medias: "",
    body: "",
    userId: "",
    queueId: "",
  });
  const [file, setFile] = useState({});
  const [fileName, setFileName] = useState("");

  const { user } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();

  useEffect(() => {
    (async () => {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs || !planConfigs.plan || !planConfigs.plan.useExternalApi) {
        toast.error(i18n.t("messagesAPI.messages.noPermission"));
        setTimeout(() => history.push(`/`), 1000);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getEndpoint = () => `${BACKEND_URL}/api/messages/send`;

  const handleSendTextMessage = async (values) => {
    const { number, body, userId, queueId } = values;
    try {
      await axios.request({
        url: getEndpoint(),
        method: "POST",
        data: { number, body, userId, queueId },
        headers: {
          "Content-type": "application/json",
          Authorization: `Bearer ${values.token}`,
        },
      });
      toast.success(i18n.t("messagesAPI.messages.success"));
    } catch (err) {
      toastError(err);
    }
  };

  const handleSendMediaMessage = async (values) => {
    try {
      const firstFile = file[0];
      const data = new FormData();
      data.append("number", values.number);
      data.append("body", values.body ? values.body : firstFile.name);
      data.append("userId", values.userId);
      data.append("queueId", values.queueId);
      data.append("medias", firstFile);
      await axios.request({
        url: getEndpoint(),
        method: "POST",
        data,
        headers: {
          "Content-type": "multipart/form-data",
          Authorization: `Bearer ${values.token}`,
        },
      });
      toast.success(i18n.t("messagesAPI.messages.success"));
    } catch (err) {
      toastError(err);
    }
  };

  const Field2 = ({ label, name, required, autoFocus }) => (
    <Field
      as={TextField}
      label={label}
      name={name}
      variant="outlined"
      size="small"
      fullWidth
      className={classes.roundedField}
      required={required}
      autoFocus={autoFocus}
    />
  );

  const renderFormMessageText = () => (
    <Formik
      initialValues={formMessageTextData}
      enableReinitialize
      onSubmit={(values, actions) => {
        setTimeout(async () => {
          await handleSendTextMessage(values);
          actions.setSubmitting(false);
          actions.resetForm();
        }, 400);
      }}
    >
      {({ isSubmitting }) => (
        <Form>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Field2
                label={i18n.t("messagesAPI.textMessage.token")}
                name="token"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Field2
                label={i18n.t("messagesAPI.textMessage.number")}
                name="number"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Field2
                label={i18n.t("messagesAPI.textMessage.body")}
                name="body"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Field2
                label={i18n.t("messagesAPI.textMessage.userId")}
                name="userId"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Field2
                label={i18n.t("messagesAPI.textMessage.queueId")}
                name="queueId"
              />
            </Grid>
            <Grid item xs={12} className={classes.sendRow}>
              <Button
                type="submit"
                variant="contained"
                disableElevation
                className={classes.sendBtn}
                disabled={isSubmitting}
                startIcon={
                  isSubmitting ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <Send size={14} strokeWidth={2} />
                  )
                }
              >
                {i18n.t("messagesAPI.form.send")}
              </Button>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );

  const renderFormMessageMedia = () => (
    <Formik
      initialValues={formMessageMediaData}
      enableReinitialize
      onSubmit={(values, actions) => {
        setTimeout(async () => {
          await handleSendMediaMessage(values);
          actions.setSubmitting(false);
          actions.resetForm();
          const input = document.getElementById("medias");
          if (input) {
            input.value = null;
          }
          setFile({});
          setFileName("");
        }, 400);
      }}
    >
      {({ isSubmitting }) => (
        <Form>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Field2
                label={i18n.t("messagesAPI.mediaMessage.token")}
                name="token"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Field2
                label={i18n.t("messagesAPI.mediaMessage.number")}
                name="number"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Field2
                label={i18n.t("messagesAPI.textMessage.body")}
                name="body"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Field2
                label={i18n.t("messagesAPI.textMessage.userId")}
                name="userId"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Field2
                label={i18n.t("messagesAPI.textMessage.queueId")}
                name="queueId"
              />
            </Grid>
            <Grid item xs={12}>
              <div className={classes.fileFieldWrap}>
                <input
                  type="file"
                  name="medias"
                  id="medias"
                  required
                  className={classes.fileInput}
                  onChange={(e) => {
                    setFile(e.target.files);
                    setFileName(e.target.files?.[0]?.name || "");
                  }}
                />
                <label htmlFor="medias" className={classes.fileBtn}>
                  <Upload size={14} strokeWidth={2} />
                  {i18n.t("messagesAPI.form.chooseFile", {
                    defaultValue: "Escolher arquivo",
                  })}
                </label>
                <span className={classes.fileHint} title={fileName || ""}>
                  {fileName ||
                    i18n.t("messagesAPI.form.noFile", {
                      defaultValue: "Nenhum arquivo selecionado",
                    })}
                </span>
              </div>
            </Grid>
            <Grid item xs={12} className={classes.sendRow}>
              <Button
                type="submit"
                variant="contained"
                disableElevation
                className={classes.sendBtn}
                disabled={isSubmitting}
                startIcon={
                  isSubmitting ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <Send size={14} strokeWidth={2} />
                  )
                }
              >
                {i18n.t("messagesAPI.form.send")}
              </Button>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );

  const renderDocsText = () => (
    <div className={classes.docs}>
      <div className={classes.docsRow}>
        <span className={classes.docsLabel}>
          {i18n.t("messagesAPI.documentation.endpoint")}
        </span>
        <span className={classes.docsValue}>{getEndpoint()}</span>
      </div>
      <div className={classes.docsRow}>
        <span className={classes.docsLabel}>
          {i18n.t("messagesAPI.documentation.method")}
        </span>
        <span className={classes.docsValue}>
          {i18n.t("messagesAPI.documentation.post")}
        </span>
      </div>
      <div className={classes.docsRow}>
        <span className={classes.docsLabel}>
          {i18n.t("messagesAPI.documentation.headers")}
        </span>
        <span className={classes.docsValue}>
          {i18n.t("messagesAPI.documentation.headersTextAuth")}
        </span>
      </div>
      <div>
        <span className={classes.docsLabel} style={{ display: "block", marginBottom: 4 }}>
          {i18n.t("messagesAPI.documentation.body")}
        </span>
        <pre className={classes.docsBody}>
          {i18n.t("messagesAPI.documentation.bodyExample")}
        </pre>
      </div>
    </div>
  );

  const renderDocsMedia = () => (
    <div className={classes.docs}>
      <div className={classes.docsRow}>
        <span className={classes.docsLabel}>
          {i18n.t("messagesAPI.documentation.endpoint")}
        </span>
        <span className={classes.docsValue}>{getEndpoint()}</span>
      </div>
      <div className={classes.docsRow}>
        <span className={classes.docsLabel}>
          {i18n.t("messagesAPI.documentation.method")}
        </span>
        <span className={classes.docsValue}>
          {i18n.t("messagesAPI.documentation.post")}
        </span>
      </div>
      <div className={classes.docsRow}>
        <span className={classes.docsLabel}>
          {i18n.t("messagesAPI.documentation.headers")}
        </span>
        <span className={classes.docsValue}>
          {i18n.t("messagesAPI.documentation.headersMediaAuth")}
        </span>
      </div>
      <div>
        <span className={classes.docsLabel} style={{ display: "block", marginBottom: 6 }}>
          {i18n.t("messagesAPI.documentation.formData")}
        </span>
        <ul className={classes.fieldsList}>
          <li>
            <code>{i18n.t("messagesAPI.documentation.formDataFields.number")}</code>
          </li>
          <li>
            <code>{i18n.t("messagesAPI.documentation.formDataFields.body")}</code>
          </li>
          <li>
            <code>{i18n.t("messagesAPI.documentation.formDataFields.userId")}</code>
          </li>
          <li>
            <code>{i18n.t("messagesAPI.documentation.formDataFields.queueId")}</code>
          </li>
          <li>
            <code>{i18n.t("messagesAPI.documentation.formDataFields.medias")}</code>
          </li>
          <li>
            <code>{i18n.t("messagesAPI.documentation.formDataFields.sendSignature")}</code>
          </li>
          <li>
            <code>{i18n.t("messagesAPI.documentation.formDataFields.closeTicket")}</code>
          </li>
        </ul>
      </div>
    </div>
  );

  return (
    <div className={classes.container}>
      <div className={classes.pageHeader}>
        <div className={classes.headerLeft}>
          <span className={classes.pageIconWrap}>
            <Code2 size={20} strokeWidth={1.8} />
          </span>
          <div>
            <div className={classes.pageTitle}>
              {i18n.t("messagesAPI.API.title")}
            </div>
            <div className={classes.pageSubtitle}>
              {i18n.t("messagesAPI.subtitle", {
                defaultValue:
                  "Integre sistemas externos para enviar mensagens via API.",
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Card 1 — Métodos & Instruções */}
      <div className={classes.card}>
        <div className={classes.cardHeader}>
          <span className={classes.cardHeaderIcon}>
            <Info size={16} strokeWidth={2} />
          </span>
          <div>
            <div className={classes.cardHeaderTitle}>
              {i18n.t("messagesAPI.API.methods.title")}
            </div>
            <div className={classes.cardHeaderSubtitle}>
              {i18n.t("messagesAPI.methodsSubtitle", {
                defaultValue:
                  "Endpoints disponíveis e regras para o número de destino.",
              })}
            </div>
          </div>
        </div>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <div className={classes.sectionTitle}>
              <ListOrdered size={13} strokeWidth={2} />
              {i18n.t("messagesAPI.API.methods.title")}
            </div>
            <ol className={classes.listInfo}>
              <li>{i18n.t("messagesAPI.API.methods.messagesText")}</li>
              <li>{i18n.t("messagesAPI.API.methods.messagesMidia")}</li>
            </ol>
          </Grid>

          <Grid item xs={12} md={6}>
            <div className={classes.sectionTitle}>
              <AlertCircle size={13} strokeWidth={2} />
              {i18n.t("messagesAPI.API.instructions.title")}
            </div>
            <div>
              <b style={{ fontSize: 13 }}>
                {i18n.t("messagesAPI.API.instructions.comments")}
              </b>
              <ul className={classes.listInfo} style={{ marginTop: 6 }}>
                <li>{i18n.t("messagesAPI.API.instructions.comments1")}</li>
                <li>
                  {i18n.t("messagesAPI.API.instructions.comments2")}
                  <ul>
                    <li>{i18n.t("messagesAPI.API.instructions.codeCountry")}</li>
                    <li>{i18n.t("messagesAPI.API.instructions.code")}</li>
                    <li>{i18n.t("messagesAPI.API.instructions.number")}</li>
                  </ul>
                </li>
              </ul>
            </div>
          </Grid>
        </Grid>
      </div>

      {/* Card 2 — Texto */}
      <div className={classes.card}>
        <div className={classes.cardHeader}>
          <span className={classes.cardHeaderIcon}>
            <MessageSquare size={16} strokeWidth={2} />
          </span>
          <div>
            <div className={classes.cardHeaderTitle}>
              {i18n.t("messagesAPI.API.text.title")}
            </div>
            <div className={classes.cardHeaderSubtitle}>
              {i18n.t("messagesAPI.API.text.instructions")}
            </div>
          </div>
        </div>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            {renderDocsText()}
          </Grid>
          <Grid item xs={12} md={6}>
            <div className={classes.testTitle}>
              <Send size={12} strokeWidth={2} />
              {i18n.t("messagesAPI.form.testSend")}
            </div>
            {renderFormMessageText()}
          </Grid>
        </Grid>
      </div>

      {/* Card 3 — Mídia */}
      <div className={classes.card}>
        <div className={classes.cardHeader}>
          <span className={classes.cardHeaderIcon}>
            <ImageIcon size={16} strokeWidth={2} />
          </span>
          <div>
            <div className={classes.cardHeaderTitle}>
              {i18n.t("messagesAPI.API.media.title")}
            </div>
            <div className={classes.cardHeaderSubtitle}>
              {i18n.t("messagesAPI.API.media.instructions")}
            </div>
          </div>
        </div>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            {renderDocsMedia()}
          </Grid>
          <Grid item xs={12} md={6}>
            <div className={classes.testTitle}>
              <Send size={12} strokeWidth={2} />
              {i18n.t("messagesAPI.form.testSend")}
            </div>
            {renderFormMessageMedia()}
          </Grid>
        </Grid>
      </div>
    </div>
  );
};

export default MessagesAPI;
